import './BalancePaySheet.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { balancePayActions, balancePaySelector } from '$store/balancePay';
import { Button, LottieIcon } from '$uikit';
import { BalancePayAction } from '$store/balancePay/interface';
import { useTranslator, useUnitName, useUserInfo } from '$hooks';
import { mainSelector } from '$store/main';
import { balanceActions } from '$store/balance';
import { roundNumber, statReachGoal } from '$utils';
import { SponsorCongratulations } from '$core/BalancePaySheet/SponsorCongratulations/SponsorCongratulations';
import { useGetAuthorId } from '$utils/useGetAuthorId';
import { useHasSubscription } from '$utils/useHasSubscription';

export const BalancePaySheet: FC = () => {
  const [popupId, setPopupId] = useState(-1);
  const t = useTranslator();
  const dispatch = useDispatch();
  const {
    isActive,
    amount,
    action,
    extraId,
    objectId,
    isAnon = false,
    period,
  } = useSelector(balancePaySelector);

  const { eurBalance } = useSelector(mainSelector);
  const canPay = eurBalance >= amount;

  const user = useUserInfo(objectId || 0);
  const userNameInstr = useUnitName(user, 'instrumental');
  const userNameGenitive = useUnitName(user, 'genitive');
  const userNameDative = useUnitName(user, 'dative');
  const { router, route } = useRoute();
  const authorId = useGetAuthorId();
  const hasSubscription = useHasSubscription(authorId);

  const handleClose = useCallback(() => {
    dispatch(balancePayActions.close());
  }, [dispatch]);

  useEffect(() => {
    if (route?.params?.pay_confirm) {
      router.navigate(
        route.name,
        {
          ...route.params,
          pay_confirm: undefined,
        },
        { replace: true },
      );
      dispatch(balancePayActions.restoreContext());
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      if (!canPay) {
        dispatch(
          balanceActions.setTopUpActive({
            topUpActive: true,
            requireTopUpAmount: amount,
          }),
        );
        dispatch(balancePayActions.close());
      }
      statReachGoal('add_to_cart', {
        item_id: `${action}_${objectId}_${extraId}`,
        price: +amount,
        item_name: action,
        currency: 'EUR',
      });
    }
  }, [isActive]);

  const caption = useMemo<string>(() => {
    if (action === BalancePayAction.MESSAGE) {
      return t('balance_pay_message', { name: userNameInstr });
    } else if (action === BalancePayAction.TIPS) {
      return t('balance_pay_tips', { name: userNameGenitive });
    } else if (action === BalancePayAction.SUBSCRIPTION_PERSONAL) {
      return t('balance_pay_sponsor_subscription', { name: userNameInstr });
    } else if (action === BalancePayAction.SUBSCRIPTION_ADULT) {
      return t('balance_pay_adult_subscription');
    } else if (action === BalancePayAction.SUBSCRIPTION_MEDIA) {
      return t('balance_pay_media_subscription');
    } else if (action === BalancePayAction.ROULETTE) {
      return t('balance_pay_roulette_spin');
    } else if (action === BalancePayAction.VALENTINE) {
      return t('balance_pay_valentine', { name: userNameDative });
    } else if (action === BalancePayAction.GIFT) {
      return t('balance_pay_gift', { name: userNameDative });
    } else if (action === BalancePayAction.APP_PURCHASE) {
      return t('balance_pay_app_purchase');
    } else {
      return '';
    }
  }, [action, objectId, t]);

  const handlePay = useCallback(() => {
    const hasSubscriptionAnimation = action === BalancePayAction.SUBSCRIPTION_PERSONAL;

    if (canPay) {
      dispatch(
        balanceActions.pay({
          amount,
          action: action!,
          objectId: objectId!,
          extraId: extraId!,
          isAnon,
          period,
          closeSheet: !hasSubscriptionAnimation,
          hideSuccessToast: hasSubscriptionAnimation,
        }),
      );
    } else {
      dispatch(
        balanceActions.setTopUpActive({
          topUpActive: true,
          requireTopUpAmount: amount,
        }),
      );
    }
  }, [canPay, action, objectId, extraId, amount, period]);

  useEffect(() => {
    if (hasSubscription) {
      setPopupId(0);
    }
  }, [hasSubscription]);

  return isActive ? (
    <BottomSheet
      isActiveControl={isActive}
      popupId={popupId}
      setPopupId={setPopupId}
      chain={[<SponsorCongratulations key={'sponsor'} />]}
      title=""
      scroll={false}
      onClose={handleClose}
    >
      <div className="BalancePaySheet">
        <div className="BalancePaySheet__info">
          <LottieIcon size={120} name="money2" />
          <div className="BalancePaySheet__amount">{roundNumber(amount, 2)} EUR</div>
          <div className="BalancePaySheet__caption">{caption}</div>
        </div>
        <div className="BalancePaySheet__confirmButton">
          <Button onClick={handlePay}>{t('balance_pay_btn')}</Button>
        </div>
      </div>
    </BottomSheet>
  ) : null;
};
